/**
 * Shared `fieldGroup`-only tree walkers for a persisted or generated Formly field tree.
 *
 * Three call sites in the onboarding-review feature each grew their own copy of "descend
 * `fieldGroup` only, test a predicate": `findReadonlyOnboardingField` in the review-inbox
 * panel, the anonymous `walk` inside `readonly-onboarding.component.ts`'s
 * `#conditionKeyPathById`, and `hostsDocumentsChecklist` in `review-flags.util.ts`. This file is
 * the one place that walk lives now.
 *
 * Deliberately `fieldGroup`-only — `fieldArray` is NOT descended. Formly's live tree resolvers
 * this mirrors (`findFieldValueById`, `searchFieldGroupsForValue`) do not descend it either, so an
 * id or type only reachable through a `fieldArray` must stay unreachable here too rather than
 * silently disagreeing with the live form.
 *
 * The bound is structural on purpose (`type`/`fieldGroup` only) rather than `FormlyFieldConfig`
 * itself: `WizardPageFieldTree` (a flattened review-page shape, `review-flags.types.ts`) and
 * `FormlyFieldConfig` both satisfy it without either importing the other.
 */

/** Minimal shape a field-tree node needs for these walkers — see the file doc above. */
export interface FieldTreeNode<T> {
  type?: unknown;
  fieldGroup?: readonly T[] | null;
}

/**
 * Defensive against persisted data rather than a live tree: a legacy or half-migrated document can
 * carry a `fieldGroup` that is not an array, or a null element in it — both are skipped rather than
 * thrown on.
 */
function asFieldArray<T>(fields: readonly T[] | null | undefined): readonly T[] {
  return Array.isArray(fields) ? fields : [];
}

/**
 * First field in the tree (pre-order: a node before its own `fieldGroup`) matching `predicate`,
 * searched depth-first. `undefined` when nothing matches.
 */
export function findInFieldTree<T extends FieldTreeNode<T>>(
  fields: readonly T[] | null | undefined,
  predicate: (field: T) => boolean
): T | undefined {
  for (const field of asFieldArray(fields)) {
    if (!field || typeof field !== 'object') continue;
    if (predicate(field)) return field;
    const nested = findInFieldTree(field.fieldGroup, predicate);
    if (nested) return nested;
  }
  return undefined;
}

/** Whether any field in the tree (the roots themselves included) matches `predicate`. */
export function someInFieldTree<T extends FieldTreeNode<T>>(
  fields: readonly T[] | null | undefined,
  predicate: (field: T) => boolean
): boolean {
  return findInFieldTree(fields, predicate) !== undefined;
}

/** Visits every field in the tree, pre-order, for a side effect (e.g. building a lookup map). */
export function forEachInFieldTree<T extends FieldTreeNode<T>>(
  fields: readonly T[] | null | undefined,
  visit: (field: T) => void
): void {
  for (const field of asFieldArray(fields)) {
    if (!field || typeof field !== 'object') continue;
    visit(field);
    forEachInFieldTree(field.fieldGroup, visit);
  }
}
